import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useProjects } from '../contexts/ProjectContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { StatusBadge } from '../components/common/StatusBadge';
import { ConfirmDeployModal } from '../components/modals/ConfirmDeployModal';
import { Deployment } from '@kaizen/shared';
import { formatDate } from '../lib/utils';
import {
  kaizenMicroTransition,
  kaizenStateTransition
} from '../lib/kaizenMotion';
import { CloudUpload, Globe, ExternalLink, RefreshCw, Eye, ShieldCheck, AlertTriangle } from 'lucide-react';

const INITIAL_DEPLOYMENTS: Deployment[] = [
  {
    id: 'dep-1',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    userId: '00000000-0000-0000-0000-000000000001',
    deviceId: '11111111-1111-1111-1111-111111111111',
    environment: 'production',
    status: 'live',
    url: 'https://kaizen-enabling-works.pages.dev',
    startedAt: new Date(Date.now() - 86400000).toISOString(),
    finishedAt: new Date(Date.now() - 86370000).toISOString(),
    durationMs: 30000,
    exitCode: 0,
    logsSummary: 'Successfully deployed to Cloudflare Pages production environment.',
    createdAt: new Date(Date.now() - 86400000).toISOString()
  },
  {
    id: 'dep-2',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    userId: '00000000-0000-0000-0000-000000000001',
    deviceId: '11111111-1111-1111-1111-111111111111',
    environment: 'preview',
    status: 'live',
    url: 'https://7f3a9e1.kaizen-enabling-works.pages.dev',
    startedAt: new Date(Date.now() - 7200000).toISOString(),
    finishedAt: new Date(Date.now() - 7182000).toISOString(),
    durationMs: 18000,
    exitCode: 0,
    logsSummary: 'Preview branch deployed successfully.',
    createdAt: new Date(Date.now() - 7200000).toISOString()
  }
];

export const DeploymentsPage: React.FC = () => {
  const { projects, activeProject } = useProjects();
  const { sendJob, device } = useRealtime();
  const [deployments, setDeployments] = useState<Deployment[]>(INITIAL_DEPLOYMENTS);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleTriggerPreview = async () => {
    if (!activeProject) {
      setActionError('No project selected');
      return;
    }
    setActionError(null);
    try {
      await sendJob({
        deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
        projectId: activeProject.id,
        type: 'deploy_preview',
        payload: {
          projectName: activeProject.cloudflareProjectName || 'preview-project',
          args: [activeProject.buildOutputDirectory]
        },
        workingDirectory: activeProject.localPath
      });
    } catch (err: any) {
      console.error('Preview deployment dispatch failed:', err);
      setActionError(`Preview deployment dispatch failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleConfirmProductionDeploy = async () => {
    if (!activeProject) {
      setActionError('No project selected');
      return;
    }
    setActionError(null);
    try {
      await sendJob({
        deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
        projectId: activeProject.id,
        type: 'deploy_production',
        payload:
          activeProject.config?.deployment?.mode === 'vercel_render'
            ? {
                projectName:
                  activeProject.name,

                targetEnvironment:
                  'production' as const,

                deploymentMode:
                  'vercel_render' as const,

                gitRemote:
                  activeProject.config.deployment.gitRemote ||
                  'origin',

                gitBranch:
                  activeProject.config.deployment.gitBranch ||
                  'main',

                vercelTeamId:
                  activeProject.config.deployment.vercel?.teamId,

                vercelProjectId:
                  activeProject.config.deployment.vercel?.projectId,

                vercelProductionUrl:
                  activeProject.config.deployment.vercel?.productionUrl,

                renderServiceId:
                  activeProject.config.deployment.render?.serviceId,

                renderHealthUrl:
                  activeProject.config.deployment.render?.healthUrl,

                renderApiKeyEnv:
                  activeProject.config.deployment.render?.apiKeyEnv ||
                  'RENDER_API_KEY'
              }
            : {
                projectName:
                  activeProject.cloudflareProjectName ||
                  'production-project',

                targetEnvironment:
                  'production' as const,

                deploymentMode:
                  'cloudflare_pages' as const,

                args: [
                  activeProject.buildOutputDirectory
                ]
              },
        workingDirectory: activeProject.localPath
      });
    } catch (err: any) {
      console.error('Production deployment dispatch failed:', err);
      setActionError(`Production deployment dispatch failed: ${err?.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="space-y-6">
      <AnimatePresence initial={false}>
        {actionError && (
          <motion.div
            initial={{
              opacity: 0,
              y: -6,
              height: 0
            }}
            animate={{
              opacity: 1,
              y: 0,
              height: 'auto'
            }}
            exit={{
              opacity: 0,
              y: -4,
              height: 0
            }}
            transition={
              kaizenStateTransition
            }
            className="overflow-hidden p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-center justify-between"
          >
            <span>{actionError}</span>
            <button
              onClick={() => setActionError(null)}
              className="text-rose-500 hover:text-rose-700 font-bold ml-2"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <CloudUpload className="w-5 h-5 text-indigo-600" />
            <span>Deployments</span>
          </h2>
          <p className="text-xs text-slate-500">
            Provider-aware deployments executed through your personal Windows laptop
          </p>
        </div>

        <div className="flex items-center gap-2">
          <motion.button
            onClick={handleTriggerPreview}
            whileHover={{
              y: -1
            }}
            whileTap={{
              scale: 0.97
            }}
            transition={
              kaizenMicroTransition
            }
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg shadow-subtle transition-colors"
          >
            <Eye className="w-3.5 h-3.5 text-indigo-600" />
            <span>Preview Deploy</span>
          </motion.button>
          <motion.button
            onClick={() => setIsConfirmModalOpen(true)}
            whileHover={{
              y: -1
            }}
            whileTap={{
              scale: 0.97
            }}
            transition={
              kaizenMicroTransition
            }
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm transition-colors"
          >
            <CloudUpload className="w-3.5 h-3.5 text-amber-400" />
            <span>Deploy Production</span>
          </motion.button>
        </div>
      </div>

      {/* Cloudflare Pages Security Banner */}
      <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-xl flex items-start gap-3 text-xs text-slate-600">
        <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold text-slate-800">Security Architecture:</span> Cloudflare API tokens and Wrangler tooling reside exclusively on your personal Windows laptop. Deployment commands are triggered as discrete jobs and executed locally. Tokens are never passed over the web or stored in Supabase tables.
        </div>
      </div>

      {/* Deployments Table */}
      <div className="acrylic-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                <th className="px-5 py-3">Environment</th>
                <th className="px-5 py-3">Project</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Deployment URL</th>
                <th className="px-5 py-3">Duration</th>
                <th className="px-5 py-3">Deployed At</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deployments.map((dep) => (
                <tr key={dep.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3 font-semibold">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-mono ${
                      dep.environment === 'production'
                        ? 'bg-purple-50 text-purple-700 border border-purple-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {dep.environment.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-800">
                    {projects.find(p => p.id === dep.projectId)?.name || dep.projectId}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={dep.status} />
                  </td>
                  <td className="px-5 py-3 font-mono">
                    {dep.url ? (
                      <a
                        href={dep.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline flex items-center gap-1"
                      >
                        <span className="truncate max-w-[200px]">{dep.url}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-600">
                    {dep.durationMs ? `${Math.round(dep.durationMs / 1000)}s` : '--'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {formatDate(dep.finishedAt || dep.startedAt)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={handleTriggerPreview}
                      className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                      title="Redeploy"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Production Confirmation Modal */}
      {activeProject && (
        <ConfirmDeployModal
          project={activeProject}
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmProductionDeploy}
        />
      )}
    </div>
  );
};
